import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { ThematicApiProvider, useThematicApi } from '../Services/ThematicApiProvider';
import { formatDateUTC } from '../utils/formatDate';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';
import axios from 'axios';
import SplitButton from '../Components/SplitButton';

export function ThematicListContent({ canEdit }) {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getThematic, bulkDeleteThematics, importThematics, exportThematics } = useThematicApi();

  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectedToggle, setClearSelectedToggle] = useState(false);

  const tableTitle = 'Thematics';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const columns = [
    { name: 'ID', selector: (row) => row.key, sortable: false },
    { name: 'Name', selector: (row) => row.name, sortable: false },
    {
      name: 'Status',
      selector: (row) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: false },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: false },
  ];

  const clearSelection = () => {
    setSelectedRows([]);
    setClearSelectedToggle((prev) => !prev);
  };

  const handleDelete = (selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) return;

    const count = selectedIds.length;
    const label = `thematic${count > 1 ? 's' : ''}`;

    showConfirmation(`Are you sure you want to delete ${count} ${label}?`, async () => {
      try {
        setLoading(true);
        await bulkDeleteThematics(selectedIds);
        clearSelection();
        await fetchItems(persistedSkip, persistedPageSize);
        showAlert('success', `${count} ${label} successfully deleted!`);
      } catch (error) {
        const serverError = axios.isAxiosError(error) ? error.response?.data : undefined;
        const msg = serverError?.error ?? serverError?.message ?? `Failed to delete ${label}!`;
        showAlert('fail', msg);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/thematic/${row.key}`, { state: { organizationId } });
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    // reset the input so selecting the same file again re-triggers the change event
    event.target.value = '';
    if (!file) return;
    setLoading(true);
    try {
      const response = await importThematics(file);
      await fetchItems(persistedSkip, persistedPageSize);
      showAlert('success', `${response.totalCount} thematic(s) were successfully imported!`);
    } catch (error) {
      const serverError = axios.isAxiosError(error) ? error.response?.data : undefined;
      const message =
        serverError?.error ??
        serverError?.message ??
        error?.message ??
        'Failed to import thematics from file!';

      // Server error may contain dozens of lines (one per invalid row).
      // Trim it so the alert stays readable and doesn't overflow the screen.
      const MAX_LINES = 8;
      const lines = message.split('\n');
      const shortMessage =
        lines.length > MAX_LINES
          ? [...lines.slice(0, MAX_LINES), `…and ${lines.length - MAX_LINES} more error(s)`].join(
              '\n',
            )
          : message;

      showAlert('fail', shortMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleExportThematics = async (type) => {
    if (type === 'selected' && (!selectedRows || selectedRows.length === 0)) return;

    try {
      setLoading(true);

      let exportParams = {};

      if (type === 'selected') {
        exportParams = {
          keys: selectedRows.map((row) => row.key),
        };
      }

      const file = await exportThematics(exportParams);
      const url = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'thematics.xlsx';

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      if (type === 'selected') {
        clearSelection();
      }
    } catch (error) {
      console.error(`Error exporting ${type} thematics:`, error);
      showAlert('fail', `Failed to export ${type} thematics!`);
    } finally {
      setLoading(false);
    }
  };

  const exportContextActions = (
    <SplitButton
      options={[
        {
          label: 'Export Selected',
          onClick: () => handleExportThematics('selected'),
        },
        {
          label: `Export All (${totalItems})`,
          onClick: () => handleExportThematics('all'),
        },
      ]}
      disabled={selectedRows.length === 0}
      variant="ghost"
      size="medium"
    />
  );

  const fetchItems = async (skip, limit) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getThematic(skip, limit);
      setItems(response.items);
      setTotalItems(response.totalCount);
    } catch (error) {
      setError(error.message);
      showAlert('fail', 'Failed to fetch thematics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(persistedSkip, persistedPageSize);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={items}
                selectableRows
                selectableRowsHighlight
                singleSelect={false}
                clearSelectedRows={clearSelectedToggle}
                onSelectedRowsChange={({ selectedRows }) => {
                  setSelectedRows(selectedRows);
                }}
                paginationPerPage={persistedPageSize}
                progressPending={loading}
                paginationTotalRows={totalItems}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                onChangePage={fetchItems}
                getRowUrl={(row) => `/organizations/${organizationId}/thematic/${row.key}`}
                contextActionsExtra={exportContextActions}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() => navigate(`/organizations/${organizationId}/thematic/new-thematic`)}
                >
                  Create New thematic
                </Button>
              )}

              {canEdit && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,.csv,.xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleImportFile}
                  />
                  <Button
                    variant="ghost"
                    size="large"
                    onClick={handleImportClick}
                    disabled={loading}
                  >
                    Import thematics
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ThematicsListPage({ canEdit }) {
  return (
    <ConfirmationProvider>
      <ThematicApiProvider>
        <ThematicListContent canEdit={canEdit} />
      </ThematicApiProvider>
    </ConfirmationProvider>
  );
}

export default ThematicsListPage;
